import { reactive, ref, computed, onMounted, toRefs } from 'vue';
import { debounce } from 'lodash';
import { useFavorites } from './useFavorites'; // Import useFavorites composable

const layoutConfig = reactive({
    ripple: false,
    darkTheme: false,
    inputStyle: 'outlined',
    menuMode: 'static',
    theme: 'lara-light-indigo',
    scale: 14,
    activeMenuItem: null
});

const layoutState = reactive({
    staticMenuDesktopInactive: true,
    overlayMenuActive: true,
    profileSidebarVisible: false,
    configSidebarVisible: false,
    staticMenuMobileActive: false,
    menuHoverActive: false,
    subSidebarActive: false
});

const mobileBreakpoint = 992; // Breakpoint for mobile view
const isMobileScreen = ref(window.innerWidth <= mobileBreakpoint);

// Reactive favorites list (from useFavorites composable)
const { favorites, addFavorite, removeFavorite, loadFavorites } = useFavorites();

const updateScreenSize = debounce(() => {
    isMobileScreen.value = window.innerWidth <= mobileBreakpoint;
}, 500);

onMounted(() => {
    window.addEventListener('resize', updateScreenSize);
    loadFavorites();  // Load favorites from localStorage when component is mounted
});

export function useLayout() {
    const changeThemeSettings = (theme, darkTheme) => {
        layoutConfig.darkTheme = darkTheme;
        layoutConfig.theme = theme;
    };

    const setScale = (scale) => {
        layoutConfig.scale = scale;
    };

    const setActiveMenuItem = (item) => {
        layoutConfig.activeMenuItem = item.value || item;
    };

    const onMenuToggle = () => {
        if (layoutConfig.menuMode === 'overlay') {
            layoutState.overlayMenuActive = !layoutState.overlayMenuActive;
        }

        if (window.innerWidth > mobileBreakpoint) {
            layoutState.staticMenuMobileActive = false;
            layoutState.staticMenuDesktopInactive = !layoutState.staticMenuDesktopInactive;
        } else {
            layoutState.staticMenuDesktopInactive = true;
            layoutState.staticMenuMobileActive = !layoutState.staticMenuMobileActive;
        }
    };

    const onSubSidebarToggle = (event) => {
        if (typeof event === 'boolean') {
            layoutState.subSidebarActive = event;
            return;
        }
        layoutState.subSidebarActive = !layoutState.subSidebarActive;
    };

    const isSubSidebarActive = computed(() => layoutState.subSidebarActive);

    const isSidebarActive = computed(() => !layoutState.staticMenuDesktopInactive || layoutState.staticMenuMobileActive);

    const isDarkTheme = computed(() => layoutConfig.darkTheme);

    return {
        layoutConfig: toRefs(layoutConfig),
        layoutState: toRefs(layoutState),
        favorites,            // Export the reactive favorites array
        addFavorite,         // Export addFavorite method
        removeFavorite,      // Export removeFavorite method
        changeThemeSettings,
        setScale,
        onMenuToggle,
        isSidebarActive,
        isDarkTheme,
        setActiveMenuItem,
        onSubSidebarToggle,
        isSubSidebarActive,
        isMobileScreen
    };
}
